import PlusIcon from "@redotech/redo-icons/plus.svg";
import type { StoreApiTokenSummary } from "@redotech/redo-partners-portal-rpc/schema/shared/api-token";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoEmptyState } from "@redotech/redo-web/arbiter-components/empty-state/redo-empty-state";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import type { RedoTableColumn } from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { RedoTableTextCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import type { Breadcrumb } from "@redotech/redo-web/breadcrumb";
import { Flex } from "@redotech/redo-web/flex";
import {
  usePageBreadcrumbs,
  usePageSubtitle,
  usePageTitle,
  usePageTitleTrailing,
} from "@redotech/redo-web/page-header-slot";
import { ClientError } from "@redotech/rpc/errors";
import { memo, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { CreateTokenModal } from "../components/create-token-modal";
import { TokenTable } from "../components/token-table";
import { type ApiScope, ScopePicker } from "./scope-picker";
import { StoreOriginBadge } from "./stores-page";
import {
  useStore,
  useStoreApiTokens,
  useStoreTokenActions,
} from "./use-stores";

const scopesColumn: RedoTableColumn<StoreApiTokenSummary> = {
  key: "scopes",
  renderHeaderCell: () => <RedoTableTextCell text="Scopes" />,
  renderNormalCell: ({ row }) => (
    <RedoTableTextCell
      text={`${row.scopes.length} ${row.scopes.length === 1 ? "scope" : "scopes"}`}
    />
  ),
};

const StoreTokenModal = memo(function StoreTokenModal({
  granted,
  create,
  onClose,
}: {
  granted: ApiScope[];
  create: (input: { name: string; scopes: ApiScope[] }) => Promise<string>;
  onClose: () => void;
}) {
  const [scopes, setScopes] = useState(granted);
  return (
    <CreateTokenModal
      canSubmit={scopes.length > 0}
      create={(name) => create({ name, scopes })}
      onClose={onClose}
      subtitle="A merchant API token for this store, limited to the scopes you pick."
      title="Create store token"
    >
      <ScopePicker granted={granted} setValue={setScopes} value={scopes} />
    </CreateTokenModal>
  );
});

const STORE_BREADCRUMBS: Breadcrumb[] = [
  { id: "stores", name: "Stores", url: "/stores", showLeadingBackIcon: true },
];

export const StoreDetailPage = memo(function StoreDetailPage() {
  const storeId = useParams().storeId ?? "";
  const store = useStore(storeId);
  const tokens = useStoreApiTokens(storeId);
  const actions = useStoreTokenActions(storeId);
  const [creating, setCreating] = useState(false);

  const origin = store.data?.origin;
  usePageTitle(store.data?.name);
  usePageSubtitle(store.data?.storeUrl ?? undefined);
  usePageTitleTrailing(
    useMemo(
      () => (origin ? <StoreOriginBadge origin={origin} /> : undefined),
      [origin],
    ),
  );
  usePageBreadcrumbs(STORE_BREADCRUMBS);

  if (store.isError) {
    const notFound =
      store.error instanceof ClientError && store.error.statusCode === 404;
    return (
      <Flex p="4xl">
        {notFound ? (
          <RedoEmptyState
            subtext="It may not exist, or your integration no longer manages it."
            text="Store not found"
          />
        ) : (
          <RedoEmptyState
            primaryButtonProps={{
              text: "Try again",
              onClick: () => void store.refetch(),
            }}
            subtext="Something went wrong loading this store."
            text="Couldn't load the store"
          />
        )}
      </Flex>
    );
  }
  if (!store.data) {
    return (
      <Flex justify="center" p="4xl">
        <RedoSpinner size="md" />
      </Flex>
    );
  }

  if (tokens.isError) {
    return (
      <Flex p="4xl">
        <RedoEmptyState
          primaryButtonProps={{
            text: "Try again",
            onClick: () => void tokens.refetch(),
          }}
          subtext="Something went wrong loading this store's API tokens."
          text="Couldn't load tokens"
        />
      </Flex>
    );
  }

  return (
    <>
      <TokenTable
        extraColumns={[scopesColumn]}
        headerButton={
          <RedoButton
            hierarchy="secondary"
            IconLeading={PlusIcon}
            onClick={() => setCreating(true)}
            size="sm"
            text="Create token"
          />
        }
        pending={tokens.isPending}
        revoke={actions.revoke}
        title="Store API tokens"
        tokens={tokens.data ?? []}
      />
      {creating && (
        <StoreTokenModal
          create={actions.create}
          granted={store.data.scopes}
          onClose={() => setCreating(false)}
        />
      )}
    </>
  );
});
